import { memoize } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';

import {
  basketLoadersFeatureKey,
  State,
} from '../reducers/basket-loaders.reducer';
import * as fromBasket from '../store';

export const selectBasketLoadersState = createSelector<
  fromBasket.State,
  [fromBasket.BasketState],
  State
>(fromBasket.selectBasketState, (state) => state[basketLoadersFeatureKey]);
type LoadersThatCanBeIgnored = {
  [key in keyof State]: key extends boolean ? State[key] : never;
};

type IgnoredLoaders = keyof Pick<
  LoadersThatCanBeIgnored,
  | 'loadingError'
  | 'downloading'
  | 'updateShippingOption'
  | 'updateBasketClientReferenceNumber'
  | 'updatingAddressIds'
  | 'updatingDeliveryIds'
  | 'updateShipInFullOption'
  | 'busyUpdatingManualDiscounts'
  | 'busyDeletingManualDiscounts'
  | 'busyLoadingMaxDiscountPercentageForOrderType'
  | 'busyLoadingMaxDiscountPercentageForProducts'
>;

export const selectBasketLoaders = createSelector<
  fromBasket.State,
  [State],
  Omit<State, IgnoredLoaders>
>(
  selectBasketLoadersState,
  ({
    loadingError,
    downloading,
    updateShippingOption,
    updateBasketClientReferenceNumber,
    updatingAddressIds,
    updateShipInFullOption,
    updatingDeliveryIds,
    busyUpdatingManualDiscounts,
    busyDeletingManualDiscounts,
    busyLoadingMaxDiscountPercentageForOrderType,
    busyLoadingMaxDiscountPercentageForProducts,
    ...loaders
  }) => loaders,
);

/**
 * Selects if the basket is loading
 */
export const selectBasketLoading = createSelector(
  selectBasketLoaders,
  (loaders) => {
    return Object.values(loaders).some((loader) => Boolean(loader));
  },
);

/**
 * Selects if the basket content is downloading
 */
export const selectBasketDownloading = createSelector(
  selectBasketLoadersState,
  (state) => state.downloading,
);

export const selectShippingOptionUpdating = createSelector(
  selectBasketLoadersState,
  (state) => state.updateShippingOption,
);

export const selectIsBasketClientReferenceNumberUpdating = createSelector(
  selectBasketLoadersState,
  (state) => state.updateBasketClientReferenceNumber,
);

export const selectUpdatingAddressIds = createSelector(
  selectBasketLoadersState,
  (state) => state.updatingAddressIds,
);

export const selectIsShopUpdating = (addressId: string) =>
  createSelector(selectUpdatingAddressIds, (ids) => ids.includes(addressId));

export const selectShipInFullOptionUpdating = createSelector(
  selectBasketLoadersState,
  (state) => state.updateShipInFullOption,
);

export const selectUpdatingDeliveryIds = createSelector(
  selectBasketLoadersState,
  (state) => state.updatingDeliveryIds,
);

export const selectIsDeliveryUpdating = memoize((deliveryId: string) =>
  createSelector(selectUpdatingDeliveryIds, (ids: string[]) =>
    ids.includes(deliveryId),
  ),
);

export const selectIsManualDiscountUpdating = createSelector(
  selectBasketLoadersState,
  (state) => state.busyUpdatingManualDiscounts,
);

export const selectIsManualDiscountDeleting = createSelector(
  selectBasketLoadersState,
  (state) => state.busyDeletingManualDiscounts,
);

export const selectIsManualDiscountBusy = createSelector(
  selectIsManualDiscountDeleting,
  selectIsManualDiscountUpdating,
  (isDeleting, isUpdating) => isDeleting || isUpdating,
);

export const selectBasketManualDiscountIsLoadingConfig = createSelector(
  selectBasketLoadersState,
  (state) =>
    state.busyLoadingMaxDiscountPercentageForOrderType ||
    state.busyLoadingMaxDiscountPercentageForProducts,
);
